import { Droppable } from '@hello-pangea/dnd';
import { Box, Stack, Typography, IconButton, Menu, MenuItem, useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Configure, Index } from 'react-instantsearch';
import { useKanbanContext } from '@/context/kanbanContext';
import { usePipelineContext } from '@/context/pipelineContext';
import { IS_DEV } from '@/utils/environment';
import { HitsKanbanColumn } from '@/components/table/HitsTableBody';
import { KanbanCard } from '../KanbanCard';
import { LoadingCard } from '../KanbanCard/LoadingCard';
import { KanbanColumnKPIFooter } from './KanbanColumnKPIFooter';
import { useKanbanFilters } from '@/hooks/useKanbanFilters';
import { useColumnControl } from '@/context/columnControlContext.jsx';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { getKanbanSortHeadCells } from '@/components/tables/DealTable/data';
import InstantSearchSortSync from '@/components/tables/InstantSearchDataGrid/InstantSearchSortSync';

export const KanbanColumn = ({ stageId, width = '250px' }) => {
	const theme = useTheme();
	const [refreshKey, setRefreshKey] = useState(0);
	const [dealCount, setDealCount] = useState(0);
	const [totalAmount, setTotalAmount] = useState(0);
	const hasLoadedRef = useRef(false);

	// State for column menu
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const menuOpen = Boolean(menuAnchorEl);

	const { pipelineId, isClaims, isEndorsement, isProspect, refreshStatus } = useKanbanContext();
	const { stages } = usePipelineContext(pipelineId);

	const { toggleColumn } = useColumnControl();
	const { getFeatureFlags } = useFeatureFlagContext();

	const showNewBusinessKPI = Boolean(getFeatureFlags('newBusinessKPI')?.active);

	const pipelineStage = stages?.find((s) => s.id === stageId || s.stageId === stageId);

	const isDeal = !(isClaims || (IS_DEV && isEndorsement));
	const isNewBusiness = pipelineId == process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const isDebtor = pipelineId == process.env.REACT_APP_PIPELINE_DEBTORS;

	const filterSchema = useKanbanFilters(stageId);

	const indexName = isProspect ? 'clients' : 'deals';

	const sortColumns = useMemo(
		() =>
			getKanbanSortHeadCells({ pipeline: pipelineId, showNewBusinessKPI }).map((cell) => ({
				field: cell.id,
				label: cell.label,
			})),
		[pipelineId, showNewBusinessKPI],
	);

	// Menu handlers
	const handleMenuClick = (event) => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setMenuAnchorEl(null);
	};

	const handleHideColumn = () => {
		toggleColumn(stageId);
		handleMenuClose();
	};

	const resetAndRefresh = () => {
		setRefreshKey((prev) => prev + 1);
	};

	useEffect(() => {
		if (refreshStatus[stageId]) {
			console.log('Triggering reset and refresh from KanbanColumn');
			setTimeout(async () => resetAndRefresh(), 2500);
		}
	}, [refreshStatus[stageId]]);

	useEffect(() => {
		if (!hasLoadedRef.current) {
			hasLoadedRef.current = true;
			setRefreshKey((prev) => prev + 1);
		}
	}, []);

	return (
		<Stack
			className='kanban-column'
			sx={{
				minWidth: width,
				maxWidth: width,
				width,
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			<Box
				sx={{
					position: 'sticky',
					top: 0,
					zIndex: 10,
					height: '48px',
					bgcolor:
						theme.palette.mode === 'dark'
							? theme.palette.darkModeHeader
							: theme.palette.background.paper,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					px: 1.5,
					borderBottom: `1px solid ${theme.palette.divider}`,
					boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
					flexShrink: 0,
				}}
			>
				<Typography
					sx={{
						fontSize: '12px',
						fontWeight: 600,
						letterSpacing: '0.3px',
						textTransform: 'uppercase',
					}}
				>
					{pipelineStage?.label ?? stageId}
				</Typography>

				<Box sx={{ display: 'flex', alignItems: 'center' }}>
					<Typography
						sx={{
							fontSize: '12px',
							fontWeight: 500,
							px: 0.75,
							py: 0.25,
						}}
					>
						{dealCount}
					</Typography>

					<IconButton
						size='small'
						onClick={handleMenuClick}
						sx={{
							p: 0.5,
							color: theme.palette.text.secondary,
							'&:hover': { bgcolor: theme.palette.action.hover },
						}}
					>
						<MoreVertIcon sx={{ fontSize: '16px' }} />
					</IconButton>

					<Menu
						anchorEl={menuAnchorEl}
						open={menuOpen}
						onClose={handleMenuClose}
						anchorOrigin={{
							vertical: 'bottom',
							horizontal: 'right',
						}}
						transformOrigin={{
							vertical: 'top',
							horizontal: 'right',
						}}
					>
						<MenuItem onClick={handleHideColumn} sx={{ fontSize: '13px' }}>
							Hide column
						</MenuItem>
					</Menu>
				</Box>
			</Box>
			<Box
				className='kanban-column-inner'
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					'&::-webkit-scrollbar': {
						width: '4px',
					},
					'&::-webkit-scrollbar-thumb': {
						backgroundColor: theme.palette.action.disabled,
						borderRadius: '4px',
					},
				}}
			>
				<Index indexName={indexName} indexId={stageId}>
					<InstantSearchSortSync indexName={indexName} columns={sortColumns} />
					<Configure {...filterSchema} hitsPerPage={10} />
					<Droppable droppableId={stageId}>
						{(droppableProvided, snapshot) => (
							<Box
								ref={droppableProvided.innerRef}
								{...droppableProvided.droppableProps}
								className={snapshot.isDraggingOver ? ' isDraggingOver' : ''}
								sx={{
									display: 'flex',
									flexDirection: 'column',
									flexGrow: 1,
									minHeight: '100%',
									borderRadius: '3px',
									// bgcolor: 'transparent',
									padding: '5px',
									'&.isDraggingOver': {
										bgcolor:
											theme.palette.mode === 'dark'
												? 'neutral.800'
												: 'rgba(224, 224, 224, 1)',
									},
								}}
							>
								<HitsKanbanColumn
									key={`kanban-column-${stageId}-${refreshKey}`}
									CardComponent={KanbanCard}
									LoadingComponent={LoadingCard}
									objectType={isProspect ? 'client' : isDeal ? 'deal' : 'ticket'}
									includeTasks={true}
									includePolicies={isDebtor}
									includeTransactions={isDebtor}
									stageId={stageId}
									onLoadMore={(total, sum) => {
										setDealCount(total);
										if (isNewBusiness && showNewBusinessKPI) {
											setTotalAmount(sum || 0);
										}
									}}
								/>
								{droppableProvided.placeholder}
							</Box>
						)}
					</Droppable>
				</Index>
			</Box>

			{isNewBusiness && showNewBusinessKPI && (
				<KanbanColumnKPIFooter totalAmount={totalAmount} />
			)}
		</Stack>
	);
};
